'use client';

import { buildSystemRolesBadgeDropdown } from './constructSystemRolesBadgeDropdown';
import BadgeDropdown from '../../../../../_components/BadgeDropdown/BadgeDropdown';
import { usePermissions } from '@modules/auth/hooks/usePermissions/usePermissions';
import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';

import { LockOutlined } from '@ant-design/icons';
import React from 'react';
import { SystemRole } from 'shared/src/authz/rolesV2';
import { MemberAccessCheckType } from 'shared/src/generated/openapi/v3/types';
import { isCriblInternalEmail } from 'shared/src/util';

/**
 * System Roles Badge Dropdown Component
 * Displays user's system roles in a badge with dropdown menu
 *
 * Only renders if:
 * - User is a Cribl internal employee (@cribl.io email)
 * - User has at least one role
 *
 * Used to show system role status in top nav
 */
export const SystemRolesBadgeDropdownComponent: React.FC = () => {
  const { user } = useCommonAuthProvider();

  // Fetch all available system roles using the permissions hook
  const userSystemPermissions = usePermissions([
    {
      accessType: MemberAccessCheckType.System,
      checks: Object.values(SystemRole),
    },
  ]);

  // Don't render if user is not a Cribl internal employee
  if (!user?.email || !isCriblInternalEmail(user.email)) {
    return null;
  }

  // Convert permissions Record to array of roles the user has
  const userRoles = Object.entries(userSystemPermissions)
    .filter(([, hasRole]) => hasRole)
    .map(([role]) => role as SystemRole);

  const badgeData = buildSystemRolesBadgeDropdown({
    userRoles,
  });

  // Don't render if user has no roles
  if (!badgeData) {
    return null;
  }

  return (
    <BadgeDropdown
      icon={<LockOutlined />}
      label={badgeData.label}
      count={badgeData.count && badgeData.count > 0 ? badgeData.count : undefined}
      tooltipTitle={badgeData.tooltipTitle}
      menuItems={badgeData.menuItems}
    />
  );
};
